import { InputRef, TableColumnType, Button, Card, DatePicker, message, notification, Popover, Space, Spin, Table, Tag, Tooltip, Typography } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { BASE_URL } from "../../../utils/constants";
import { CalendarOutlined, CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined, EyeOutlined, PlusOutlined, SendOutlined, SettingOutlined } from "@ant-design/icons";
import { MobileNotification, MobileNotificationIndex } from "../../../utils/interfaces";
import DeleteModal from "../../GlobalComponents/Modals/DeleteModal";
import { SubmitHandler, useForm } from "react-hook-form";
import MobileNotificationCreateModal from "../../../Components/MobileNotificationPageComp/Modals/MobileNotificationCreateModal";
import MobileNotificationEditModal from "../../../Components/MobileNotificationPageComp/Modals/MobileNotificationEditModal";
import styles from '../../../global.module.css'
import BasicDisplayModal from "../../GlobalComponents/Modals/BasicDisplayModal";
import BasicActionModal from "../../GlobalComponents/Modals/BasicActionModal";
import React, {useRef} from "react";
import { FilterDropdownProps, TablePaginationConfig } from "antd/es/table/interface";
import Search from "antd/es/input/Search";
import {
  SearchOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { useHasAction } from "../../../hooks/useHasAction";
import dayjs, { Dayjs } from "dayjs";

const MobileNotificationView: React.FC = () => {
  const canAddMobileNotification: boolean = useHasAction("mobile_notification_add_notification");
  const canEditMobileNotification: boolean = useHasAction("mobile_notification_edit_notification");
  const canDeleteMobileNotification: boolean = useHasAction("mobile_notification_delete_notification");
  const canSendMobileNotification: boolean = useHasAction("mobile_notification_send_notification");

  const [loading, setLoading] = useState<boolean>(false);
  const [mobileNotificationData, setMobileNotificationData] = useState<MobileNotification[]>([]);
  const [loadingButtons, setLoadingButtons] = useState<boolean>(false);

  const [showDeleteMobileNotificationModal, setShowDeleteMobileNotificationModal] = useState<boolean>(false);
  const [showCreateMobileNotificationModal, setShowCreateMobileNotificationModal] = useState<boolean>(false);
  const [showEditMobileNotificationModal, setShowEditMobileNotificationModal] = useState<boolean>(false);
  const [showViewMobileNotificationModal, setShowViewMobileNotificationModal] = useState<boolean>(false);
  const [showSendMobileNotificationModal, setShowSendMobileNotificationModal] = useState<boolean>(false);


  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const searchInput = useRef<InputRef>(null);

  // Tracks whether any column filter is currently applied - antd's Table
  // reports this via onChange regardless of which mechanism (filterDropdown
  // search box or a plain `filters` list) drove it. Drives the filter-chips
  // row in the card title (see filterChips below).
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const handleTableChange = (_pagination: TablePaginationConfig, filters: Record<string, any>) => {
    setActiveFilters(filters);
  };


  const [deleteMobileNotification, setDeleteMobileNotification] = useState<MobileNotification | null>(null);
  const [viewMobileNotification, setViewMobileNotification] = useState<MobileNotification | null>(null);
  const [sendMobileNotification, setSendMobileNotification] = useState<MobileNotification | null>(null);

  const [api, contextHolder] = notification.useNotification();

  // antd's virtual Table needs a concrete pixel height (rc-virtual-list uses
  // it to compute how many rows to render) - a CSS `calc()` string works for
  // a plain scroll container but not here, so track the equivalent number.
  const [tableScrollY, setTableScrollY] = useState(() => window.innerHeight - 255);
  useEffect(() => {
    const onResize = () => setTableScrollY(window.innerHeight - 255);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const {
    control: createMobileNotificationControl,
    handleSubmit: createMobileNotificationHandleSubmit,
    formState: { errors: createMobileNotificationErrors, isValid: createMobileNotificationIsValid },
    reset: createMobileNotificationReset
  } = useForm<MobileNotification>({mode: "onChange"});

  const {
    control: editMobileNotificationControl,
    handleSubmit: editMobileNotificationHandleSubmit,
    formState: { errors: editMobileNotificationErrors, isValid: editMobileNotificationIsValid, isDirty: editMobileNotificationIsDirty },
    reset: editMobileNotificationReset
  } = useForm<MobileNotification>({mode: "onChange"});

  useEffect(() => {
    handleGetMobileNotification();
  }, []);


  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void, close: () => void) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
    close();
  };

  // One color per filterable column - used both for that column's chips in
  // the title bar and for the little dot next to the column's own header,
  // so a chip and the header it came from are visually traceable at a
  // glance. Same scheme as the other list pages' filter bars.
  const COLUMN_FILTER_COLORS: Record<string, string> = {
    created_at: "#52c41a",
    notes: "#1677ff",
    sent_status: "#faad14",
  };

  const COLUMN_FILTER_LABELS: Record<string, string> = {
    created_at: "Created At",
    notes: "Notes",
    sent_status: "Production Sent Status",
  };

  // "Created At" packs its whole range into one selectedKeys entry
  // ("startISO|endISO") rather than two separate values, since antd treats
  // multiple selectedKeys values as OR'd together (row matches ANY of
  // them) - wrong for a range, which needs both bounds evaluated together
  // (AND). Declared here (rather than next to getCreatedAtFilterProps)
  // since filterChips, below, needs it too.
  const CREATED_AT_RANGE_SEPARATOR = "|";

  const parseCreatedAtRangeKey = (raw: unknown): [Dayjs, Dayjs] | null => {
    if (typeof raw !== "string" || !raw.includes(CREATED_AT_RANGE_SEPARATOR)) return null;
    const [start, end] = raw.split(CREATED_AT_RANGE_SEPARATOR);
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    return startDate.isValid() && endDate.isValid() ? [startDate, endDate] : null;
  };

  // Small colored dot appended to a column's title while it has an active
  // filter - same color as that column's chips in the title bar.
  const renderColumnTitle = (key: string, label: string) => (
    <Space size={6}>
      <span>{label}</span>
      {Array.isArray(activeFilters[key]) && activeFilters[key].length > 0 && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            display: "inline-block",
            background: COLUMN_FILTER_COLORS[key],
          }}
        />
      )}
    </Space>
  );

  // Drops one value from one column's filter, leaving any other selected
  // values on that same column untouched.
  const removeFilterValue = (key: string, value: any) => {
    setActiveFilters((prev) => {
      const current = prev[key];
      if (!Array.isArray(current)) return prev;
      const next = current.filter((v) => v !== value);
      const updated = { ...prev };
      if (next.length > 0) {
        updated[key] = next;
      } else {
        delete updated[key];
      }
      return updated;
    });
    if (searchedColumn === key) {
      setSearchText("");
      setSearchedColumn("");
    }
  };

  // Every currently applied column filter, as one flat list of removable
  // chips - shown in the card title (see the filter-chips row below).
  const filterChips = useMemo(() => {
    const chips: { key: string; label: string; color: string; onRemove: () => void }[] = [];
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (!Array.isArray(values)) return;
      values.forEach((value) => {
        const range = key === "created_at" ? parseCreatedAtRangeKey(value) : null;
        const displayValue = range
          ? `${range[0].format("YYYY-MM-DD")} → ${range[1].format("YYYY-MM-DD")}`
          : key === "sent_status"
          ? (value ? "SENT" : "NOT SENT")
          : value;
        chips.push({
          key: `${key}-${value}`,
          label: `${COLUMN_FILTER_LABELS[key] ?? key}: ${displayValue}`,
          color: COLUMN_FILTER_COLORS[key] ?? "default",
          onRemove: () => removeFilterValue(key, value),
        });
      });
    });
    return chips;
  }, [activeFilters, searchedColumn]);// eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () => {
    setActiveFilters({});
    setSearchText("");
    setSearchedColumn("");
  };

  const getColumnSearchProps = (
    dataIndex: MobileNotificationIndex,
      displayMapper?: (record: MobileNotification) => string
  ): TableColumnType<MobileNotification> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Search
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onSearch={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "flex" }}
          allowClear={{
            clearIcon: (
              <CloseOutlined
                onClick={() => clearFilters && handleReset(clearFilters, close)}
              />
            ),
          }}
        />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <>
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      </>
    ),
    // Controlled so the filter-chips row (see filterChips) can clear this
    // column's search from outside the column's own dropdown.
    filteredValue: activeFilters[dataIndex as string] ?? null,
    onFilter: (value, record) => {
      const fieldValue = displayMapper
        ? displayMapper(record)
        : // @ts-ignore
          record[dataIndex]?.toString();

      return fieldValue
        ?.toLowerCase()
        .includes((value as string).toLowerCase()) ?? false;
    },

    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },

    render: (_text, record) => {
      const displayValue = displayMapper
        ? displayMapper(record)
        : // @ts-ignore
          _text?.toString();

      return searchedColumn === dataIndex && searchText ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={displayValue || ""}
        />
      ) : (
        displayValue
      );
    },
  });

  const getCreatedAtFilterProps = (): TableColumnType<MobileNotification> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      const range = parseCreatedAtRangeKey(selectedKeys[0]);
      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <DatePicker.RangePicker
            value={range}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                const startKey = dates[0].startOf("day").toISOString();
                const endKey = dates[1].endOf("day").toISOString();
                setSelectedKeys([`${startKey}${CREATED_AT_RANGE_SEPARATOR}${endKey}`]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ marginBottom: 8, display: "flex" }}
          />
          <Space>
            <Button type="primary" size="small" style={{ width: 90 }} onClick={() => confirm()}>
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filteredValue: activeFilters.created_at ?? null,
    onFilter: (value, record) => {
      const range = parseCreatedAtRangeKey(value);
      if (!range || !record.created_at) return false;
      const created = dayjs(record.created_at as unknown as string);
      return created.isValid() && !created.isBefore(range[0]) && !created.isAfter(range[1]);
    },
  });



  const tableColumns = [
    {
      title: renderColumnTitle("created_at", "Created At"),
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      ...getCreatedAtFilterProps(),
    },
    {
      title: "Sent At",
      dataIndex: "sent_at",
      key: "sent_at",
      width: 200,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type: string) => type && <Tag color="blue">{type}</Tag>,
    },
    {
      title: renderColumnTitle("notes", "Notes"),
      dataIndex: "notes",
      key: "notes",
      width: 220,
      ...getColumnSearchProps("notes"),
    },
    {
      title: renderColumnTitle("sent_status", "Production Sent Status"),
      dataIndex: "sent_status",
      key: "sent_status",
      width: 200,
      render: (sent: boolean) =>
        sent ? <Tag color="green" icon={<CheckOutlined />}>Sent</Tag>
      : <Tag color="red" icon={<CloseOutlined />}>Not Sent</Tag>,
      filters: [
        { text: "SENT", value: true },
        { text: "NOT SENT", value: false },
      ],
      filteredValue: activeFilters.sent_status ?? null,
      onFilter: (value: boolean | React.Key, record: MobileNotification) => record.sent_status === value,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      width: 100,
      render: (_v: any, record: MobileNotification) => {

        const content = (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              disabled={!canDeleteMobileNotification}
              onClick={() => {
                toggleDeleteMobileNotificationModal(true);
                setDeleteMobileNotification(record);
              }}
              style={{ textAlign: "left" }}
            >
              Delete Notification
            </Button>
            <Button
              type="text"
              icon={<SendOutlined/>}
              disabled={!canSendMobileNotification}
              onClick={async () => {
                handleSendMobileNotification(record, 'dev');
              }}
              style={{ textAlign: "left" }}
            >
              Send DEVELOPEMENT
            </Button>
            <Button
              type="text"
              icon={<SendOutlined/>}
              disabled={!canSendMobileNotification}
              onClick={async () => {
                toggleSendMobileNotificationModal(true);
                setSendMobileNotification(record);
              }}
              style={{ textAlign: "left" }}
            >
              Send PRODUCTION
            </Button>
            <Button
              type="text"
              icon={<EditOutlined/>}
              disabled={!canEditMobileNotification}
              onClick={async () => {
                toggleEditMobileNotificationModal(true);
                editMobileNotificationReset(record);
              }}
              style={{ textAlign: "left" }}
            >
              Edit Notification
            </Button>
          </div>
        );

        const noSettingsActionsAvailable = !canEditMobileNotification && !canSendMobileNotification && !canDeleteMobileNotification;
        const settingsDisabled = record.sent_status || noSettingsActionsAvailable;
        const settingsTooltip = record.sent_status
          ? "Notification already sent"
          : noSettingsActionsAvailable
          ? "No actions available"
          : "Settings";

        return (
          <div onClick={(e) => e.stopPropagation()} style={{gap: 10, display: "flex", alignItems: "center"}}>
            <Tooltip
              title="View Notification"
              trigger={'hover'}
            >
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => {
                  toggleViewMobileNotificationModal(true);
                  setViewMobileNotification(record);
                }}
                style={{ textAlign: "left" }}
              />
            </Tooltip>
            <Popover
              content={content}
              trigger={settingsDisabled ? [] : 'hover'}
            >
              <Tooltip title={settingsTooltip} trigger={'hover'}>
                <Button
                  type="default"
                  icon={<SettingOutlined style={{ fontSize: 16 }} />}
                  disabled={settingsDisabled}
                />
              </Tooltip>
            </Popover>
          </div>
        );
      },
    }
  ];

  const toggleDeleteMobileNotificationModal = (makeVisible: boolean) => {
    if(makeVisible){
      setShowDeleteMobileNotificationModal(true);
    } else {
      setShowDeleteMobileNotificationModal(false);
    }
  }

  const toggleCreateMobileNotificationModal = (makeVisible: boolean) => {
    if(makeVisible){
      setShowCreateMobileNotificationModal(true);
    } else {
      setShowCreateMobileNotificationModal(false);
      createMobileNotificationReset();
    }
  }

  const toggleEditMobileNotificationModal = (makeVisible: boolean) => {
    if(makeVisible){
      setShowEditMobileNotificationModal(true);
    } else {
      setShowEditMobileNotificationModal(false);
      editMobileNotificationReset();
    }
  }

  const toggleViewMobileNotificationModal = (makeVisible: boolean) => {
    if(makeVisible){
      setShowViewMobileNotificationModal(true);
    } else {
      setShowViewMobileNotificationModal(false);
      setViewMobileNotification(null);
    }
  }

  const toggleSendMobileNotificationModal = (makeVisible: boolean) => {
    if(makeVisible){
      setShowSendMobileNotificationModal(true);
    } else {
      setShowSendMobileNotificationModal(false);
      setSendMobileNotification(null);
    }
  }

  const handleGetMobileNotification = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}get_mobile_notification`);
      setMobileNotificationData(response.data.content.data);
    } catch (err) {
      message.error("Failed to fetche Mobile Notifications!");
    } finally {
      setLoading(false);
    }
  };
   

  const handleCreateMobileNotification: SubmitHandler<MobileNotification> = async (data) => {
    setLoading(true);
    console.log(data);
    try {
      await axios.post(`${BASE_URL}create_mobile_notification`,{
        title: data.title,
        body: data.body,
        type: data.type
      });
      api.success({
        title: 'Create Successful',
        description: `Mobile notification created successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } catch (err) {
      api.error({
        title: 'Create Failed',
        description: `Failed to create mobile notification.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoading(false);
      await handleGetMobileNotification();
      toggleCreateMobileNotificationModal(false);
    }
  };

  const handleUpdateMobileNotification: SubmitHandler<MobileNotification> = async (mobileNotification) => {
    setLoading(true);
    try {
      await axios.post(`${BASE_URL}update_mobile_notification/${mobileNotification.id}`, {
        title: mobileNotification.title,
        body: mobileNotification.body,
        type: mobileNotification.type
      });
      await handleGetMobileNotification();
      api.success({
        title: 'Update Successful',
        description: `Mobile notification added successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } catch (err: any) {
      api.error({
        title: 'Update Failed',
        description: `Failed to update mobile notification`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoading(false);
      toggleEditMobileNotificationModal(false);
    }
  };

  const handleDeleteMobileNotification = async (mobileNotification: MobileNotification | null) => {
    if (mobileNotification === null) return;
    setLoadingButtons(true);
    try {
      await axios.post(`${BASE_URL}delete_mobile_notification/${mobileNotification.id}`);
      await handleGetMobileNotification();
      api.success({
        title: 'Delete Successful',
        description: `Mobile Notification deleted successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } catch (err) {
      api.error({
        title: 'Delete Failed',
        description: `Failed to delete Mobile notification.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoadingButtons(false);
      toggleDeleteMobileNotificationModal(false);
      setDeleteMobileNotification(null);
    }
  };

  const handleSendMobileNotification = async (mobileNotification: MobileNotification | null, appVariant: string | null) => {
    if (mobileNotification === null || appVariant === null) return;
    setLoadingButtons(true);
    try {
      await axios.post(`${BASE_URL}send_mobile_notification/${mobileNotification.id}`, {
        appVariant: appVariant
      });
      await handleGetMobileNotification();
      api.success({
        title: 'Sending Successful',
        description: `Mobile Notification was sent successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } catch (err) {
      api.error({
        title: 'Sending Failed',
        description: `Failed to send Mobile notification.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoadingButtons(false);
      toggleSendMobileNotificationModal(false);
    }
  };

  

  return (
    <div>
      {contextHolder}
      <div>
        <Card
          className={styles.globalCard}
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span style={{ flexShrink: 0 }}>Mobile Notifications</span>
              {filterChips.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, padding: "2px 0" }}>
                    {filterChips.map((chip) => (
                      <Tag
                        key={chip.key}
                        color={chip.color}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          chip.onRemove();
                        }}
                        style={{ flexShrink: 0 }}
                      >
                        {chip.label}
                      </Tag>
                    ))}
                  </div>
                  <Button size="small" type="link" onClick={clearAllFilters} style={{ flexShrink: 0 }}>
                    Clear all
                  </Button>
                </div>
              )}
              <span style={{ display: 'flex', gap: '5px', flexShrink: 0, marginLeft: filterChips.length > 0 ? 0 : "auto" }}>
              <Button icon={<PlusOutlined />} onClick={() => toggleCreateMobileNotificationModal(true)} type="primary" disabled={!canAddMobileNotification}>Create Notification</Button>
              </span>
            </div>
          }
        >
          <Spin size="large" spinning={loading}>
            <div style={{ flex: 1}}>
              <Table
                columns={tableColumns}
                dataSource={mobileNotificationData}
                pagination={false}
                scroll={{x : 1000, y: tableScrollY}}
                virtual
                rowKey="id"
                className='horizontally-synced-table'
                onChange={handleTableChange}
              />
            </div>
          </Spin>
        </Card>
      </div>
      <MobileNotificationCreateModal
        showCreateMobileNotificationModal={showCreateMobileNotificationModal}
        toggleCreateMobileNotificationModal={toggleCreateMobileNotificationModal}
        onSubmit={handleCreateMobileNotification}
        errors={createMobileNotificationErrors}
        control={createMobileNotificationControl}
        isValid={createMobileNotificationIsValid}
        handleSubmit={createMobileNotificationHandleSubmit}
        isSubmitting={loading}
      />
      <MobileNotificationEditModal
        showEditMobileNotificationModal={showEditMobileNotificationModal}
        toggleEditMobileNotificationModal={toggleEditMobileNotificationModal}
        onSubmit={handleUpdateMobileNotification}
        errors={editMobileNotificationErrors}
        control={editMobileNotificationControl}
        isValid={editMobileNotificationIsValid}
        isDirty={editMobileNotificationIsDirty}
        handleSubmit={editMobileNotificationHandleSubmit}
        isSubmitting={loading}
      />
      <BasicDisplayModal
        showModal={showViewMobileNotificationModal}
        onClose={() => toggleViewMobileNotificationModal(false)}
        content={
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Type</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue">{viewMobileNotification?.type}</Tag>
              </div>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Title</Typography.Text>
              <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "12px 16px", marginTop: 4, fontSize: 14 }}>
                {viewMobileNotification?.title}
              </div>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Body</Typography.Text>
              <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "12px 16px", marginTop: 4, fontSize: 14 }}>
                {viewMobileNotification?.body}
              </div>
            </div>
            <div style={{ display: "flex", gap: 32 }}>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Created At</Typography.Text>
                <div style={{ fontSize: 13, marginTop: 2 }}>{viewMobileNotification?.created_at ? String(viewMobileNotification.created_at) : "—"}</div>
              </div>
              {viewMobileNotification?.sent_status && (
                <div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>Sent At</Typography.Text>
                  <div style={{ fontSize: 13, marginTop: 2 }}>{viewMobileNotification?.sent_at ? String(viewMobileNotification.sent_at) : "—"}</div>
                </div>
              )}
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Status</Typography.Text>
              <div style={{ marginTop: 4 }}>
                {viewMobileNotification?.sent_status
                  ? <Tag color="green" icon={<CheckOutlined />}>Sent</Tag>
                  : <Tag color="red" icon={<CloseOutlined />}>Not Sent</Tag>}
              </div>
            </div>
          </div>
        }
      />
      <BasicActionModal
        showModal={showSendMobileNotificationModal}
        onClose={() => toggleSendMobileNotificationModal(false)}
        title={
          <span style={{ color: "#cf1322", display: "flex", alignItems: "center", gap: 8 }}>
            <ExclamationCircleOutlined />
            Send to PRODUCTION
          </span>
        }
        content={
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Title</Typography.Text>
              <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "12px 16px", marginTop: 4, fontSize: 14 }}>
                {sendMobileNotification?.title}
              </div>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Body</Typography.Text>
              <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "12px 16px", marginTop: 4, fontSize: 14 }}>
                {sendMobileNotification?.body}
              </div>
            </div>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Type</Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue">{sendMobileNotification?.type}</Tag>
              </div>
            </div>
          </div>
        }
        textOk="Send to Production"
        functionOk={() => { handleSendMobileNotification(sendMobileNotification, 'prod'); }}
        okDanger
        loadingOk={loadingButtons}
        disabledOk={loadingButtons}
        disabledCancel={loadingButtons}
      />
      <DeleteModal
        showModal={showDeleteMobileNotificationModal}
        onClose={() => {
          toggleDeleteMobileNotificationModal((loadingButtons) ? true : false);
          if(!loadingButtons){
            setDeleteMobileNotification(null);
          }
        }}
        onDelete={() => handleDeleteMobileNotification(deleteMobileNotification)}
        loadingDelete={loadingButtons}
        disableDelete={loadingButtons}
        disabledCancel={loadingButtons}
        title="Are you sure you want to delete the following Mobile Notification?"
        content={
          <div>
            <span>Title:{" "}<strong>{deleteMobileNotification?.title}</strong></span>
            <br />
            <span>Type:{" "}<strong>{deleteMobileNotification?.type}</strong></span>
          </div>
        }
      />
    </div>
  );
}

export default MobileNotificationView;